/**
 * BottomTabBar.jsx — Mobile bottom navigation bar
 *
 * 5 primary tabs + "More" menu overlay for secondary pages.
 * Only rendered when isMobile is true (< 900px).
 */

import { useState, useCallback, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Badge } from '@mui/material';
import { useRBAC } from '../../hooks/useRBAC';
import { useAuth } from '../../hooks/useAuth';
import { useSettings } from '../../hooks/useSettings';
import { useTessieStatus } from '../../hooks/useTessie';
import { useTodoSummary } from '../../hooks/useTodos';
import {
  Home as HomeIcon,
  Work as JobsIcon,
  Description as QuotesIcon,
  CreditCard as PayrollIcon,
  CalendarMonth as CalendarIcon,
  MoreHoriz as MoreIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  AttachMoney as InvoicesIcon,
  Calculate as CalculatorIcon,
  Shield as LegalIcon,
  Settings as SettingsIcon,
  Campaign as OutreachIcon,
  DirectionsCar as CarIcon,
  School as HowToIcon,
  SmartDisplay as RotoTubeIcon,
  ViewCarousel as CarouselIcon,
  Mic as VoicenotesIcon,
  Link as LinksIcon,
  AccountTree as ProjectsIcon,
  MapsHomeWork as OpenHouseIcon,
  Checklist as TodoIcon,
} from '@mui/icons-material';
import { useFocus } from '../../hooks/useFocus';
import { navKey } from '../../lib/focusRegistry';
import FocusSwitch from './FocusSwitch';

const TAB_HEIGHT = 56;

const PRIMARY_TABS = [
  { path: '/', label: 'Home', icon: HomeIcon, exact: true },
  { path: '/jobs', label: 'Jobs', icon: JobsIcon, feature: 'jobs', level: 'view_assigned' },
  { path: '/quotes', label: 'Quotes', icon: QuotesIcon, feature: 'quotes', level: 'view_assigned' },
  { path: '/payroll', label: 'Payroll', icon: PayrollIcon, feature: 'payroll', level: 'own_total' },
  { path: '/calendar', label: 'Calendar', icon: CalendarIcon, feature: 'calendar', level: 'own_schedule' },
];

// Every employee has a to-do list. It lives in More, unless the role sees fewer
// than 5 primary tabs (crew see 4): then it takes the free slot, after Home.
const TODO_TAB = { path: '/todos', label: 'To Do', icon: TodoIcon, color: '#38BDF8' };
const PRIMARY_MAX = 5;

const MORE_ITEMS = [
  TODO_TAB,
  { path: '/rototube', label: 'RotoTube', icon: RotoTubeIcon, color: '#EF4444' },
  { path: '/carousel', label: 'Carousel', icon: CarouselIcon, color: '#4FBFCB' },
  { path: '/howto', label: 'How To', icon: HowToIcon, color: '#10B981', feature: 'wiki', level: 'view' },
  { path: '/people', label: 'People', icon: PeopleIcon, color: '#2DD4BF', feature: 'crm', level: 'view_no_financials' },
  { path: '/invoices', label: 'Invoices', icon: InvoicesIcon, color: '#4ADE80', feature: 'invoices', level: 'view_assigned' },
  { path: '/calculator', label: 'Calculator', icon: CalculatorIcon, color: '#FBBF24', feature: 'calculator', level: 'own_earnings' },
  { path: '/legal', label: 'Legal', icon: LegalIcon, color: '#CBD5E1', feature: 'legal', level: 'own_contracts' },
  { path: '/outreach', label: 'Outreach', icon: OutreachIcon, color: '#F472B6', feature: 'outreach', level: 'full' },
  { path: '/openhouse', label: 'Open Houses', icon: OpenHouseIcon, color: '#E879F9', tool: 'openhouse' },
  { path: '/voicenotes', label: 'Voice Notes', icon: VoicenotesIcon, color: '#F97316', tool: 'voicenotes' },
  { path: '/links', label: 'Links', icon: LinksIcon, color: '#13B5EA', tool: 'links' },
  { path: '/projects', label: 'Projects', icon: ProjectsIcon, color: '#A78BFA', tool: 'projects' },
  { path: '/vehicle', label: 'Vehicle', icon: CarIcon, color: '#60A5FA', tool: 'vehicle' },
  { path: '/settings', label: 'Settings', icon: SettingsIcon, color: '#D1D5DB' },
];

const badgeSx = (badge, size) => ({
  '& .MuiBadge-badge': {
    fontSize: '0.6rem',
    height: size,
    minWidth: size,
    ...(badge.color === 'default' ? { bgcolor: 'grey.600', color: 'common.white' } : {}),
  },
});

export default function BottomTabBar() {
  const [showMore, setShowMore] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAccess, hasToolAccess } = useRBAC();
  const { profile } = useAuth();
  const { data: settings } = useSettings();
  const vehicleAccess = hasToolAccess('vehicle', settings);
  const { data: tessieStatus } = useTessieStatus({ enabled: !!vehicleAccess });
  const unreviewedDrives = tessieStatus?.unreviewedCount || 0;
  const { data: todoSummary } = useTodoSummary({ enabled: !!profile && !profile.is_external });
  const todoOpen = todoSummary?.openCount || 0;
  const todoOverdue = (todoSummary?.overdueCount || 0) > 0;
  // Count badges per path. Grey has no MUI palette colour, so 'default' gets explicit colours.
  const badges = {
    '/vehicle': { count: unreviewedDrives, color: 'warning' },
    '/todos': { count: todoOpen, color: todoOverdue ? 'error' : 'default' },
  };
  const { isHidden } = useFocus();
  // Focus hides tools, except the screen you're on.
  const focusVisible = useCallback(
    (t) => !isHidden(navKey(t.path)) || location.pathname === t.path
      || (t.path !== '/' && location.pathname.startsWith(t.path + '/')),
    [isHidden, location.pathname],
  );

  const checkPerm = useCallback((t) => {
    if (profile?.is_external) return t.path === '/projects';
    if (t.tool) return hasToolAccess(t.tool, settings);
    if (t.feature && t.level) return hasAccess(t.feature, t.level, settings);
    return true;
  }, [hasAccess, hasToolAccess, settings, profile]);

  const visiblePrimaryTabs = useMemo(() => {
    // External contributors only have access to /projects — give them a direct tab
    // instead of an empty bar (no PRIMARY_TABS entry passes checkPerm for them).
    if (profile?.is_external) {
      return [{ path: '/projects/me', label: 'My Work', icon: ProjectsIcon }];
    }
    const tabs = PRIMARY_TABS.filter((t) => checkPerm(t) && focusVisible(t));
    if (tabs.length < PRIMARY_MAX && focusVisible(TODO_TAB)) tabs.splice(1, 0, TODO_TAB);
    return tabs;
  }, [checkPerm, profile, focusVisible]);
  const todoIsPrimary = visiblePrimaryTabs.includes(TODO_TAB);
  const visibleMoreItems = useMemo(
    () => MORE_ITEMS.filter((t) => checkPerm(t) && focusVisible(t) && !(t === TODO_TAB && todoIsPrimary)),
    [checkPerm, focusVisible, todoIsPrimary],
  );
  const moreBadge = !todoIsPrimary && visibleMoreItems.includes(TODO_TAB) ? badges['/todos'] : null;

  const isActive = useCallback(
    (path, exact) => {
      if (exact) return location.pathname === path;
      return location.pathname === path || location.pathname.startsWith(path + '/');
    },
    [location.pathname],
  );

  // Check if current page is one of the "More" pages (highlight More icon)
  const isMoreActive = visibleMoreItems.some((item) => isActive(item.path, false));

  const handleMoreNav = useCallback(
    (path) => {
      setShowMore(false);
      navigate(path);
    },
    [navigate],
  );

  return (
    <>
      {/* More overlay */}
      {showMore && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1400,
            bgcolor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
          onClick={() => setShowMore(false)}
        >
          <Box
            data-track-area="tabbar-more"
            onClick={(e) => e.stopPropagation()}
            sx={{
              bgcolor: 'background.paper',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              pb: `calc(${TAB_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 8px)`,
              pt: 1,
              px: 2,
            }}
          >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, px: 0.5 }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                More
              </Typography>
              <IconButton size="small" onClick={() => setShowMore(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ mb: 1, mx: -1 }}>
              <FocusSwitch tone="light" />
            </Box>

            {/* Grid of items */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 1.5,
              }}
            >
              {visibleMoreItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path, false);
                const badge = badges[item.path];
                return (
                  <Box
                    key={item.path}
                    data-track={navKey(item.path)}
                    data-focus={navKey(item.path)}
                    onClick={() => handleMoreNav(item.path)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.5,
                      py: 1.5,
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: active ? 'rgba(19, 181, 234, 0.08)' : 'transparent',
                      '&:active': { bgcolor: 'rgba(19, 181, 234, 0.15)' },
                    }}
                  >
                    {badge?.count > 0 ? (
                      <Badge badgeContent={badge.count} color={badge.color} max={99} sx={badgeSx(badge, 16)}>
                        <Icon sx={{ fontSize: 28, color: active ? 'primary.main' : item.color }} />
                      </Badge>
                    ) : (
                      <Icon sx={{ fontSize: 28, color: active ? 'primary.main' : item.color }} />
                    )}
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: active ? 600 : 500,
                        color: active ? 'primary.main' : 'text.secondary',
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}

      {/* Tab bar */}
      <Box
        data-track-area="tabbar"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1201,
          minHeight: TAB_HEIGHT,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'stretch',
          pb: 'env(safe-area-inset-bottom, 0px)',
          boxSizing: 'content-box',
        }}
      >
        {visiblePrimaryTabs.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab.path, tab.exact);
          const badge = badges[tab.path];
          return (
            <Box
              key={tab.path}
              component={NavLink}
              to={tab.path}
              data-focus={navKey(tab.path)}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2px',
                textDecoration: 'none',
                color: active ? 'primary.main' : 'text.disabled',
                transition: 'color 150ms',
                '&:active': { color: 'primary.dark' },
                // Active dot indicator
                position: 'relative',
                '&::before': active
                  ? {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      width: 24,
                      height: 3,
                      borderRadius: '0 0 3px 3px',
                      bgcolor: 'primary.main',
                    }
                  : {},
              }}
            >
              {badge?.count > 0 ? (
                <Badge badgeContent={badge.count} color={badge.color} max={99} sx={badgeSx(badge, 14)}>
                  <Icon sx={{ fontSize: 22 }} />
                </Badge>
              ) : (
                <Icon sx={{ fontSize: 22 }} />
              )}
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: active ? 600 : 400,
                  lineHeight: 1,
                  letterSpacing: '0.02em',
                }}
              >
                {tab.label}
              </Typography>
            </Box>
          );
        })}

        {/* More tab */}
        <Box
          data-track="tabbar.more"
          onClick={() => setShowMore((v) => !v)}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2px',
            cursor: 'pointer',
            color: showMore || isMoreActive ? 'primary.main' : 'text.disabled',
            transition: 'color 150ms',
            '&:active': { color: 'primary.dark' },
            position: 'relative',
            '&::before':
              isMoreActive && !showMore
                ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    width: 24,
                    height: 3,
                    borderRadius: '0 0 3px 3px',
                    bgcolor: 'primary.main',
                  }
                : {},
          }}
        >
          {moreBadge?.count > 0 ? (
            <Badge badgeContent={moreBadge.count} color={moreBadge.color} max={99} sx={badgeSx(moreBadge, 14)}>
              <MoreIcon sx={{ fontSize: 22 }} />
            </Badge>
          ) : (
            <MoreIcon sx={{ fontSize: 22 }} />
          )}
          <Typography
            sx={{
              fontSize: '0.6rem',
              fontWeight: showMore || isMoreActive ? 600 : 400,
              lineHeight: 1,
              letterSpacing: '0.02em',
            }}
          >
            More
          </Typography>
        </Box>
      </Box>
    </>
  );
}

export { TAB_HEIGHT };
